import PropTypes from 'prop-types'
import {Modal, FileUpload, Button, ButtonWithIcon, Icon} from '@/components/atoms'
import {formatExtensionsLabel} from '@/shared/utils/commonTemplate'
import useUploadCsvModal from './useUploadCsvModal'
import './_uploadCsvModal.scss'

const UploadCsvModal = ({
  showModal,
  title,
  subtitle = '',
  fileLabel,
  testIdPrefix,
  onClose,
  onUpload,
  onDownloadTemplate,
  validateFile,
  invalidFileErrorMessage,
  acceptedExtensions = '.csv',
  modalClassName = '',
  fileUploadClassName = '',
  fileUploadContainerClassName = '',
}) => {
  const extensionsLabel = formatExtensionsLabel(acceptedExtensions)
  const resolvedErrorMessage = invalidFileErrorMessage || `File Format is not valid. Only ${extensionsLabel} files are supported.`
  const {
    selectedFile,
    isUploading,
    fileValidationError,
    apiErrors,
    fileUploadWrapperRef,
    handleFileSelection,
    handleUpload,
    handleClose,
    handleDelete,
    handleUploadZoneClick,
    handleUploadZoneKeyDown,
    handleUploadZoneTouchEnd,
    handleFileUploadError,
  } = useUploadCsvModal({onUpload, onClose, invalidFileErrorMessage: resolvedErrorMessage, validateFile, acceptedExtensions})

  return (
    <Modal
      showModal={showModal}
      title={title}
      text={subtitle}
      isDark
      type="M"
      className={`upload-csv-modal-wrapper ${selectedFile || fileValidationError || apiErrors ? 'upload-csv-modal-wrapper--expanded' : ''} ${modalClassName}`}
      showPrimaryButton={false}
      showSecondaryButton={false}
      onCloseModal={handleClose}
      content={
        <div data-testid={`${testIdPrefix}-upload-modal-content`} className="flex flex-col gap-4 py-2">
          <div
            ref={fileUploadWrapperRef}
            role="button"
            tabIndex={0}
            aria-label={`Upload ${extensionsLabel} file`}
            onClick={handleUploadZoneClick}
            onKeyDown={handleUploadZoneKeyDown}
            onTouchEnd={handleUploadZoneTouchEnd}
          >
            <FileUpload
              acceptedExtensions={acceptedExtensions}
              description={`Drag and drop your file here (only ${extensionsLabel}; max 5 MB)`}
              label={fileLabel}
              className={fileUploadClassName}
              containerClassName={fileUploadContainerClassName}
              value={selectedFile ? [selectedFile] : []}
              onUpload={handleFileSelection}
              onUploadDelete={handleDelete}
              onUploadError={handleFileUploadError}
              feedbackType={fileValidationError ? 'error' : 'info'}
              helperText={fileValidationError}
              isDark
              multiple={false}
              dataTestId={`${testIdPrefix}-file-upload`}
            />
          </div>
          {apiErrors && (
            <div className="upload-csv-api-errors" data-testid={`${testIdPrefix}-upload-api-errors`}>
              <div className="upload-csv-api-errors-title">
                <Icon alt="Error" icon="error" size="small" className="upload-csv-api-errors-icon" />
                <span>Upload failed with the following errors:</span>
              </div>
              <ul className="upload-csv-api-errors-list">
                {apiErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
          {selectedFile && (
            <div className="upload-csv-selected-file-row">
              <div className="flex items-center gap-2">
                <Icon alt="Selected file" icon="description" size="small" className="upload-csv-selected-file-icon" />
                <span className="text-white">{`${selectedFile?.name}`}</span>
              </div>
              <button
                type="button"
                aria-label="Delete selected file"
                data-testid={`${testIdPrefix}-selected-file-delete`}
                className="upload-csv-delete-button"
                onClick={handleDelete}
              >
                <Icon alt="Delete selected file" icon="delete" size="small" className="upload-csv-selected-file-icon" />
              </button>
            </div>
          )}
          <div className="flex items-center justify-between border-t border-gray-700 pt-4">
            <ButtonWithIcon
              id={`${testIdPrefix}-upload-download-template`}
              handleButton={onDownloadTemplate}
              iconLeading="file_download"
              uiSchema={{
                title: 'Download Template',
                variant: 'tertiary',
                isDark: true,
                size: 'medium',
              }}
            />
            <div className="flex gap-3">
              <Button
                id={`${testIdPrefix}-upload-cancel`}
                handleButton={handleClose}
                uiSchema={{title: 'Cancel', variant: 'secondary', isDark: true, size: 'medium'}}
              />
              <Button
                id={`${testIdPrefix}-upload-submit`}
                handleButton={handleUpload}
                uiSchema={{
                  title: isUploading ? 'Uploading...' : 'Upload',
                  variant: 'primary',
                  isDark: true,
                  size: 'medium',
                  disabled: !selectedFile || isUploading,
                }}
              />
            </div>
          </div>
        </div>
      }
    />
  )
}

UploadCsvModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  fileLabel: PropTypes.string.isRequired,
  testIdPrefix: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  onDownloadTemplate: PropTypes.func.isRequired,
  validateFile: PropTypes.func,
  invalidFileErrorMessage: PropTypes.string,
  acceptedExtensions: PropTypes.string,
  modalClassName: PropTypes.string,
  fileUploadClassName: PropTypes.string,
  fileUploadContainerClassName: PropTypes.string,
}

export default UploadCsvModal
